/**
 * PedigreePrintout — the printed document for a hypothetical cross.
 *
 * This is the ONLY markup that reaches paper, from either page. The shared-link
 * page renders it as its visible content; the in-app page renders it hidden and
 * lets it take over at print time, with its own interactive UI suppressed.
 *
 * Both pages producing the identical print DOM is the point: when each page
 * printed its own layout, small structural differences (an extra wrapper, a
 * toolbar, different container padding) changed how the sheet was laid out and
 * the two outputs drifted.
 *
 * Props:
 *   data:  the /api/breeding/hypothetical payload ({ display, coi, linebreeding })
 *   title: optional heading; defaults to the cross's own name
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import PedigreeTable from './PedigreeTable';
import CoiCard from './CoiCard';
import LinebreedingCard from './LinebreedingCard';
import { DEFAULT_WATERMARK_TEXT } from './PedigreePrintLayer';

export default function PedigreePrintout({ data, title, note }) {
  if (!data) return null;

  return (
    <Box className="pedigree-printout">
      <Typography variant="overline" color="text.secondary">
        {DEFAULT_WATERMARK_TEXT} LLC
      </Typography>
      <Typography variant="h4">{title || data.display?.horse_name}</Typography>
      {note && (
        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
          {note}
        </Typography>
      )}

      <Divider sx={{ mt: 1, mb: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {/* A column on screen; three equal columns on paper — see pedigreePrintSx. */}
          <Stack spacing={2} className="pedigree-analysis">
            <CoiCard coi={data.coi} />
            <LinebreedingCard linebreeding={data.linebreeding} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={8} className="pedigree-chart">
          <Card variant="outlined">
            <CardContent>
              <PedigreeTable data={data.display} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
